import { useMemo, type CSSProperties } from 'react';
import { useAppSelector } from '../../store';
import { computeLineDiff, type TimelineStep, type DiffHunk } from '@repancy/core';
import { testIds } from '../../lib/testIds';

interface UnifiedDiffProps {
  originalSessionId: string;
  forkedSessionId: string;
}

function getStepsText(
  stepIds: string[],
  entities: Record<string, TimelineStep>,
  sessionId: string,
): string {
  return stepIds
    .map((id) => entities[id])
    .filter((s): s is TimelineStep => s !== undefined && s.sessionId === sessionId)
    .map((s) => s.content)
    .join('\n');
}

const containerStyle: CSSProperties = {
  border: '2px solid',
  borderColor: '#808080 #dfdfdf #dfdfdf #808080',
  backgroundColor: '#ffffff',
  overflow: 'auto',
  maxHeight: 500,
};

const tableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontFamily: "'Courier New', Courier, monospace",
  fontSize: 12,
  lineHeight: '18px',
};

const lineNumStyle: CSSProperties = {
  width: 40,
  padding: '1px 6px',
  textAlign: 'right',
  color: '#808080',
  borderRight: '1px solid #ece9d8',
  userSelect: 'none',
  verticalAlign: 'top',
};

function rowBg(type: DiffHunk['type']): string {
  switch (type) {
    case 'added':
      return '#e6ffe6';
    case 'removed':
      return '#ffe6e6';
    default:
      return 'transparent';
  }
}

function prefixChar(type: DiffHunk['type']): string {
  switch (type) {
    case 'added':
      return '+';
    case 'removed':
      return '-';
    default:
      return ' ';
  }
}

function prefixColor(type: DiffHunk['type']): string {
  switch (type) {
    case 'added':
      return '#008000';
    case 'removed':
      return '#cc0000';
    default:
      return '#808080';
  }
}

export function UnifiedDiff({ originalSessionId, forkedSessionId }: UnifiedDiffProps) {
  const stepIds = useAppSelector((s) => s.timelines.steps.ids);
  const stepEntities = useAppSelector((s) => s.timelines.steps.entities);

  const originalText = useMemo(
    () => getStepsText(stepIds, stepEntities, originalSessionId),
    [stepIds, stepEntities, originalSessionId],
  );
  const forkedText = useMemo(
    () => getStepsText(stepIds, stepEntities, forkedSessionId),
    [stepIds, stepEntities, forkedSessionId],
  );

  const hunks = useMemo(() => computeLineDiff(originalText, forkedText), [originalText, forkedText]);

  // Build line numbers for both sides
  const rows = useMemo(() => {
    let origLine = 0;
    let newLine = 0;
    return hunks.map((hunk) => {
      let origNum: number | null = null;
      let newNum: number | null = null;
      if (hunk.type === 'unchanged') {
        origLine++;
        newLine++;
        origNum = origLine;
        newNum = newLine;
      } else if (hunk.type === 'removed') {
        origLine++;
        origNum = origLine;
      } else {
        newLine++;
        newNum = newLine;
      }
      return { hunk, origNum, newNum };
    });
  }, [hunks]);

  const contentCellStyle = (type: DiffHunk['type']): CSSProperties => ({
    padding: '1px 8px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    backgroundColor: rowBg(type),
  });

  const prefixStyle = (type: DiffHunk['type']): CSSProperties => ({
    width: 16,
    padding: '1px 4px',
    textAlign: 'center',
    fontWeight: 'bold',
    color: prefixColor(type),
    backgroundColor: rowBg(type),
    userSelect: 'none',
  });

  return (
    <div style={containerStyle} data-testid={testIds.timeline.unified}>
      <table style={tableStyle}>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              <td style={{ ...lineNumStyle, backgroundColor: rowBg(row.hunk.type) }}>
                {row.origNum ?? ''}
              </td>
              <td style={{ ...lineNumStyle, backgroundColor: rowBg(row.hunk.type) }}>
                {row.newNum ?? ''}
              </td>
              <td style={prefixStyle(row.hunk.type)}>
                {prefixChar(row.hunk.type)}
              </td>
              <td style={contentCellStyle(row.hunk.type)}>
                {row.hunk.content || ' '}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
